import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { LuCircleCheck, LuTriangleAlert, LuX } from '@platform/icon';

import {
  DESKTOP_COMPLETE_QUERY,
  parseCompleteResult,
  type DesktopCompleteResult,
} from '../lib/complete-result';

const COPY = {
  success: {
    title: 'XCOST에 로그인했습니다',
    body: '프로그램으로 돌아가 작업을 이어가세요. 이 창은 닫아도 됩니다.',
  },
  denied: {
    title: '로그인을 취소했습니다',
    body: '필요하면 프로그램에서 로그인을 다시 시작하세요. 이 창은 닫아도 됩니다.',
  },
  invalid: {
    title: '로그인 요청이 맞지 않습니다',
    body: '이 창을 닫고 프로그램에서 로그인을 다시 시작하세요.',
  },
} as const satisfies Record<DesktopCompleteResult, { title: string; body: string }>;

const MARK = {
  success: {
    icon: <LuCircleCheck className="h-7 w-7" aria-hidden />,
    className: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300',
  },
  denied: {
    icon: <LuX className="h-7 w-7" aria-hidden />,
    className: 'bg-neutral-100 text-neutral-600 dark:bg-white/10 dark:text-neutral-300',
  },
  invalid: {
    icon: <LuTriangleAlert className="h-7 w-7" aria-hidden />,
    className: 'bg-amber-50 text-amber-800 dark:bg-amber-500/15 dark:text-amber-200',
  },
} as const satisfies Record<DesktopCompleteResult, { icon: ReactNode; className: string }>;

/**
 * 데스크톱 로그인 완료 — `/desktop/complete`. 로그인 없이 열린다.
 *
 * XCOST 루프백이 code 를 받은 즉시 302 로 이 화면에 보낸다. 주소창이 루프백(127.0.0.1)에
 * 남지 않으므로 새로고침·탭 복원 때 리스너가 닫혀 있어도 오류 화면이 뜨지 않는다.
 * 쿼리는 고정 문구를 고르는 데만 쓴다.
 */
export function DesktopCompleteView() {
  const [searchParams] = useSearchParams();
  const result = parseCompleteResult(searchParams.get(DESKTOP_COMPLETE_QUERY));
  const { title, body } = COPY[result];
  const mark = MARK[result];

  return (
    <div className="mx-auto w-full max-w-md py-16 text-center">
      <span
        className={`mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full ${mark.className}`}
      >
        {mark.icon}
      </span>
      <h1 className="text-2xl font-bold -tracking-[0.02em] text-adobe-dark dark:text-neutral-100">
        {title}
      </h1>
      <p className="mt-3 text-sm leading-6 text-adobe-muted dark:text-neutral-400">{body}</p>
    </div>
  );
}
